/** Console's plain-text contract, also implemented by console_message_text in SQL.
 * Breaks/paragraph ends become newlines; strip tags, then decode the five writer
 * entities once (&amp; last). No HTML is returned or inserted into a live DOM.
 */
export function messageText(html: string): string {
  return html
    .replace(/<br\s*\/?\s*>|<\/(?:p|div|li)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .trim();
}
export function quoteExcerpt(html: string): string {
  const line = messageText(html).split('\n')[0]?.trim() ?? '';
  const chars = Array.from(line);
  return chars.length > 90 ? chars.slice(0, 89).join('') + '…' : line;
}
export interface MessageCursor {
  id: string;
  created_at: string;
}
export function compareMessages(a: MessageCursor, b: MessageCursor): number {
  // Date.parse keeps milliseconds only; PostgreSQL keeps six fractional digits.
  // Compare the remaining microseconds before breaking a real timestamp tie by UUID.
  const micros = (time: string) =>
    Number((time.match(/\.(\d+)/)?.[1] ?? '').padEnd(6, '0').slice(3, 6));
  return (
    Date.parse(b.created_at) - Date.parse(a.created_at) ||
    micros(b.created_at) - micros(a.created_at) ||
    b.id.localeCompare(a.id)
  );
}
export function mergeMessages<T extends MessageCursor>(old: T[], incoming: T[]): T[] {
  return [...new Map([...old, ...incoming].map((row) => [row.id, row])).values()].sort(
    compareMessages,
  );
}
export function searchReady(query: string, changedAt: number, now: number): boolean {
  const length = Array.from(query.trim()).length;
  return length >= 2 && length <= 200 && now - changedAt >= 250;
}
export async function jumpToMessage<T extends MessageCursor>(
  target: string,
  initial: T[],
  hasMore: boolean,
  read: (cursor: MessageCursor) => Promise<{ messages: T[]; has_more: boolean }>,
  cancelled: () => boolean,
  onPage?: (messages: T[], hasMore: boolean) => void,
): Promise<{
  status: 'found' | 'cancelled' | 'unavailable' | 'stalled' | 'ceiling';
  messages: T[];
  hasMore: boolean;
}> {
  let messages = initial;
  const result = (status: 'found' | 'cancelled' | 'unavailable' | 'stalled' | 'ceiling') => ({
    status,
    messages,
    hasMore,
  });
  for (let pages = 0; ; pages++) {
    if (cancelled()) return result('cancelled');
    if (messages.some((row) => row.id === target)) return result('found');
    if (!hasMore) return result('unavailable');
    if (pages === 20) return result('ceiling');
    const cursor = messages.at(-1);
    if (!cursor) return result('stalled');
    const page = await read(cursor);
    if (cancelled()) return result('cancelled');
    if (page.messages.length === 0 && !page.has_more) {
      hasMore = false;
      return result('unavailable');
    }
    const merged = mergeMessages(messages, page.messages);
    if (merged.length === messages.length || merged.at(-1)?.id === cursor.id)
      return result('stalled');
    messages = merged;
    hasMore = page.has_more;
    onPage?.(messages, hasMore);
  }
}
